/**
 * Monogram avatars for testimonials without a traveler photo.
 *
 * Replaces the old company-logo fallback: stamping the Central Holidays logo
 * on every review made the reviews read as company-authored rather than as
 * real travelers.
 */

/**
 * Up to two initials from the signature ("John Doe" → "JD",
 * "Margaret" → "MA"). Falls back to "?" when the name is unusable.
 */
export function getInitials(signature: string): string {
  const parts = (signature ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * On-brand gradients only — brand red through charcoal, all dark enough to
 * carry white initials at AA contrast. Neighbouring cards look distinct
 * without drifting off the palette the way free-hue HSL would.
 */
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #df1b23, #8e0f16)",
  "linear-gradient(135deg, #3f3d47, #16151a)",
  "linear-gradient(135deg, #a8171f, #4a0a0e)",
  "linear-gradient(135deg, #6b4f52, #2b1e20)",
  "linear-gradient(135deg, #b23a41, #5e161b)",
  "linear-gradient(135deg, #4c5561, #1c2127)",
];

/** Deterministic per signature, so the same person keeps the same colour. */
export function getAvatarGradient(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 33 + seed.charCodeAt(i)) | 0;
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}
